import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { instance } from "api/apiBase";
import { setInboxQueryClient } from "./client";

const TOKEN_QUERY_KEY = ["INBOX_AUTH_TOKEN"];

interface InboxAuthToken {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export const useInboxToken = ({
  enabled = true
}: { enabled?: boolean } = {}) => {
  const queryClient = useQueryClient();
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Inject queryClient into the axios client on mount
  useEffect(() => {
    setInboxQueryClient(queryClient);
  }, [queryClient]);

  const query = useQuery({
    queryKey: TOKEN_QUERY_KEY,
    queryFn: async (): Promise<InboxAuthToken> => {
      const response = await instance.post<InboxAuthToken>(
        "/communication/v1/auth/partner"
      );
      return response.data;
    },
    staleTime: 1000 * 60 * 50,
    retry: 1,
    enabled
  });

  const scheduleRefresh = useCallback(
    (remainingSeconds: number) => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);

      const refreshIn = Math.max(remainingSeconds * 1000, 0);

      refreshTimeoutRef.current = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: TOKEN_QUERY_KEY });
      }, refreshIn);
    },
    [queryClient]
  );

  useEffect(() => {
    if (!query.data?.expires_in) return;

    // Calculate elapsed time since token was fetched
    const elapsed = (Date.now() - query.dataUpdatedAt) / 1000;
    // Calculate remaining time until refresh (1 minute before expiry)
    const remaining = Math.max(query.data.expires_in - 60 - elapsed, 0);

    scheduleRefresh(remaining);

    return () => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    };
  }, [query.data?.expires_in, query.dataUpdatedAt, scheduleRefresh]);

  return {
    ...query,
    accessToken: query.data?.access_token
  };
};
